import React from 'react';
import { Button } from '@/components/ui/button';
import { MessageCircle, AlertCircle, UserX, ShieldCheck, Crown, ArrowLeft, Camera } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ChatAccessAction } from '@/core/BusinessRulesEngine/types';

interface ChatBlockedStateProps {
  message?: string;
  username?: string;
  isLoading?: boolean;
  missingRequirements?: string[];
  actions?: ChatAccessAction[];
  nextActionUrl?: string;
  nextActionLabel?: string;
  secondaryActionUrl?: string;
  secondaryActionLabel?: string;
  isUserBlocked?: boolean;
}

const getActionIcon = (label: string) => {
  if (label.toLowerCase().includes('premium') || label.toLowerCase().includes('assinar')) {
    return <Crown className="w-4 h-4 mr-2" />;
  }
  if (label.toLowerCase().includes('verific')) {
    return <ShieldCheck className="w-4 h-4 mr-2" />;
  }
  if (label.toLowerCase().includes('foto')) {
    return <Camera className="w-4 h-4 mr-2" />;
  }
  return null;
};

const ChatBlockedState = ({
  message = "para desbloquear o chat",
  username,
  isLoading = false,
  missingRequirements = [],
  actions = [],
  nextActionUrl,
  nextActionLabel,
  secondaryActionUrl,
  secondaryActionLabel,
  isUserBlocked = false
}: ChatBlockedStateProps) => {
  const navigate = useNavigate();

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-black text-white p-6">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-luxury-pinkBright"></div>
        <p className="mt-4 text-white/60">Carregando seu perfil...</p>
      </div>
    );
  }

  // User blocked state
  if (isUserBlocked) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-black text-white p-6">
        <div className="max-w-md w-full space-y-6 text-center">
          <div className="flex justify-center">
            <div className="p-6 rounded-full bg-white/10">
              <UserX className="h-16 w-16 text-white/60" />
            </div>
          </div>
          
          <div className="space-y-3">
            <h1 className="text-2xl font-semibold text-white/90">
              Conversa Indisponível
            </h1>
            <p className="text-white/60 leading-relaxed">
              Esta conversa não está mais disponível.
              <br />
              O usuário optou por não receber mensagens suas.
            </p>
          </div>

          <Button 
            variant="outline" 
            onClick={() => navigate('/main/chats')}
            className="w-full border-white/20 hover:bg-white/5 text-white"
          >
            Voltar aos Chats
          </Button>

          <p className="text-xs text-white/40 mt-6">
            Respeitamos a privacidade e preferências de todos os usuários.
          </p>
        </div>
      </div>
    );
  }

  // Render action buttons based on actions array
  const renderActions = () => {
    // If we have structured actions from BRE
    if (actions.length > 0) {
      return (
        <div className="flex flex-col gap-3 pt-4">
          {actions.map((action, index) => {
            const isHighlighted = action.highlight;
            const isPrimary = action.primary;
            
            if (isHighlighted) {
              // Primary highlighted action (Premium) - most prominent
              return (
                <Button 
                  key={index}
                  variant="brand-gradient"
                  onClick={() => navigate(action.url)} 
                  className="w-full shadow-lg shadow-luxury-pinkBright/20" 
                  size="lg"
                >
                  {getActionIcon(action.label)}
                  {action.label}
                </Button>
              );
            }
            
            if (isPrimary && !isHighlighted) {
              // Primary but not highlighted (single action for Sponsored)
              return (
                <Button 
                  key={index}
                  variant="brand-gradient"
                  onClick={() => navigate(action.url)} 
                  className="w-full" 
                  size="lg"
                >
                  {getActionIcon(action.label)}
                  {action.label}
                </Button>
              );
            }
            
            // Secondary action - subtle
            return (
              <Button 
                key={index}
                variant="ghost-dark" 
                onClick={() => navigate(action.url)} 
                className="w-full"
              >
                {getActionIcon(action.label)}
                {action.label}
              </Button>
            );
          })}
          
          {/* Always add back button */}
          <Button 
            variant="ghost-dark" 
            onClick={() => navigate(-1)} 
            className="w-full text-white/40"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
        </div>
      );
    }
    
    // Fallback to legacy props
    return (
      <div className="flex flex-col gap-3 pt-4">
        {nextActionUrl && nextActionLabel && (
          <Button 
            variant="brand-gradient"
            onClick={() => navigate(nextActionUrl)} 
            className="w-full" 
            size="lg"
          >
            {nextActionLabel}
          </Button>
        )}
        
        <Button 
          variant="ghost-dark" 
          onClick={() => navigate(-1)} 
          className="w-full"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
      </div>
    );
  };

  // Default blocked state with dynamic actions
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-black text-white p-6">
      <div className="max-w-md w-full space-y-6 text-center">
        <div className="flex justify-center">
          <div className="p-6 rounded-full bg-luxury-pinkBright/20">
            <MessageCircle className="h-16 w-16 text-luxury-pinkBright" />
          </div>
        </div>
        
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-white">Chat Bloqueado</h1>
          <p className="text-white/60">
            Complete os requisitos abaixo {message}:
          </p>
        </div>

        {/* Missing requirements list */}
        {missingRequirements.length > 0 && (
          <div className="space-y-3">
            {missingRequirements.map((requirement, index) => (
              <div key={index} className="flex items-start gap-3 p-4 bg-white/5 rounded-lg border border-white/10 text-left">
                <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-white/80">{requirement}</p>
              </div>
            ))}
          </div>
        )}

        {/* Dynamic action buttons */}
        {renderActions()}

        <p className="text-xs text-white/40 mt-8">
          Estes requisitos garantem que apenas usuários verificados possam usar o chat,
          mantendo a plataforma segura para todos.
        </p>
      </div>
    </div>
  );
};

export default ChatBlockedState;
